import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { imageProblem, isNewConversationShortcut, pastedImageFiles, pastedImageName, shortcutLabel, truncateFilename } from "../lib/reporting_ask_composer.mjs"

// Drafts live only in this tab's memory, never in browser storage. Turbo visits
// retain them; reload/sign-out clears them. Scope by viewer and partner.
const drafts = new Map()
let viewer

export default class extends Controller {
  static targets = ["input", "form", "submit", "draftField", "search", "searchForm", "draftList", "historyItem", "emptyHistory", "rail", "railToggle", "scroll",
    "newLink", "shortcut", "attachments", "attachmentTemplate", "attachmentError"]
  static values = { scope: String, conversation: String, title: String, draft: String, newUrl: String, directUploadUrl: String }

  connect() {
    const user = this.scopeValue.split(":")[0]
    if (viewer !== user) drafts.clear()
    viewer = user
    if (!drafts.has(this.scopeValue)) drafts.set(this.scopeValue, new Map())
    this.drafts = drafts.get(this.scopeValue)
    this.key = this.conversationValue ? `conversation-${this.conversationValue}` : this.draftValue || `draft-${crypto.randomUUID()}`
    this.draftValue = this.key
    if (this.hasDraftFieldTarget) this.draftFieldTarget.value = this.key
    if (this.hasInputTarget && this.drafts.has(this.key)) this.inputTarget.value = this.drafts.get(this.key).text
    this.uploads = new Map()
    ;(this.drafts.get(this.key)?.images || []).forEach(image => this.addAttachment(image))
    this.shortcutTargets.forEach(target => { target.textContent = shortcutLabel(navigator.platform, target.dataset.keys) })
    this.saveDraft()
    if (this.conversationValue && this.hasScrollTarget) this.scrollTarget.scrollTop = this.scrollTarget.scrollHeight
  }

  disconnect() {
    clearTimeout(this.searchTimer)
    this.saveDraft()
    // Uploads still running when the page changes are dropped; finished ones live on in the draft.
    this.uploads?.forEach(upload => upload.element.remove())
    this.uploads?.clear()
  }

  saveDraft() {
    if (!this.hasInputTarget || !this.drafts) return
    const text = this.inputTarget.value
    const images = this.attachedImages()
    if (text.trim() || images.length) {
      this.drafts.set(this.key, { text, images, conversation: this.conversationValue, title: this.titleValue })
    } else this.drafts.delete(this.key)
    // A screenshot alone is a question.
    this.inputTarget.required = images.length === 0 && !this.uploads?.size
    this.inputTarget.style.height = "auto"
    this.inputTarget.style.height = `${Math.min(this.inputTarget.scrollHeight, 140)}px`
    this.refreshDrafts()
  }

  refreshDrafts() {
    if (!this.drafts || !this.hasDraftListTarget) return
    const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    this.draftListTarget.replaceChildren()
    const entries = [...this.drafts]
    if (!this.conversationValue && !this.drafts.has(this.key)) entries.unshift([this.key, { text: "" }])
    entries.forEach(([key, draft]) => {
      if (draft.conversation) return
      const title = draft.text.trim().replace(/\s+/g, " ").slice(0, 60) || (draft.images?.length ? "Screenshot question" : "New conversation")
      if (!title.toLowerCase().includes(query)) return
      const link = document.createElement("a")
      const url = new URL(this.newUrlValue, window.location.origin)
      url.searchParams.set("draft", key)
      link.href = url.href
      link.dataset.turboFrame = "_top"
      link.className = "ask-history-item"
      if (key === this.key) link.setAttribute("aria-current", "page")
      const heading = document.createElement("span")
      heading.className = "ask-history-title"
      heading.textContent = title
      link.append(heading)
      if (draft.text.trim() || draft.images?.length) {
        const status = document.createElement("small")
        status.textContent = "Draft"
        link.append(status)
      }
      link.addEventListener("click", () => this.saveDraft())
      this.draftListTarget.append(link)
    })
    this.historyItemTargets.forEach(item => {
      const label = item.querySelector("[data-draft-label]")
      if (label) label.hidden = !this.drafts.has(`conversation-${item.dataset.conversationId}`)
    })
    if (this.hasEmptyHistoryTarget) this.emptyHistoryTarget.hidden = this.draftListTarget.children.length > 0
  }

  newConversation(event) {
    this.saveDraft()
    const url = new URL(this.newUrlValue, window.location.origin)
    url.searchParams.set("draft", `draft-${crypto.randomUUID()}`)
    event.currentTarget.href = url.href
  }

  search() {
    clearTimeout(this.searchTimer)
    this.refreshDrafts()
    this.searchTimer = setTimeout(() => this.searchFormTarget.requestSubmit(), 250)
  }

  suggest(event) {
    if (this.submitTarget.disabled) return
    this.inputTarget.value = event.currentTarget.dataset.question
    this.saveDraft()
    this.formTarget.requestSubmit()
  }

  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
    event.preventDefault()
    if (!this.submitTarget.disabled) this.formTarget.requestSubmit()
  }

  // ⌘⇧O / Ctrl+Shift+O (and ⌘N / Ctrl+N where the browser allows it) starts a new conversation.
  shortcut(event) {
    if (event.defaultPrevented || !isNewConversationShortcut(event) || !this.hasNewLinkTarget) return
    event.preventDefault()
    this.newLinkTarget.click()
  }

  // Images pasted into the question upload straight away (Active Storage direct upload);
  // the form submits their signed ids. Pasted text is left to the browser.
  paste(event) {
    const files = pastedImageFiles(event.clipboardData)
    if (!files.length) return
    event.preventDefault()
    this.showAttachmentError("")
    files.forEach(file => {
      const problem = imageProblem(file, this.attachmentCount())
      if (problem) return this.showAttachmentError(problem)
      this.upload(new File([file], pastedImageName(file), { type: file.type }))
    })
  }

  upload(file) {
    const id = crypto.randomUUID()
    const element = this.addAttachment({ name: file.name, previewUrl: URL.createObjectURL(file) }, { uploading: true, id })
    const upload = new DirectUpload(file, this.directUploadUrlValue)
    this.uploads.set(id, { element })
    this.saveDraft()
    upload.create((error, blob) => {
      if (!this.uploads.has(id)) return // removed while uploading
      this.uploads.delete(id)
      element.classList.remove("is-uploading")
      if (error) {
        element.classList.add("is-failed")
        element.querySelector("[data-attachment-status]").textContent = "Upload failed"
        this.showAttachmentError("An image didn't upload. Remove it and paste it again.")
      } else {
        element.dataset.signedId = blob.signed_id
        element.querySelector("input").value = blob.signed_id
        element.querySelector("input").disabled = false
        element.querySelector("[data-attachment-status]").textContent = ""
      }
      this.saveDraft()
    })
  }

  addAttachment({ name, previewUrl, signedId }, { uploading = false, id = null } = {}) {
    const element = this.attachmentTemplateTarget.content.firstElementChild.cloneNode(true)
    element.dataset.name = name
    element.dataset.previewUrl = previewUrl
    if (id) element.dataset.uploadId = id
    element.classList.toggle("is-uploading", uploading)
    element.setAttribute("aria-label", name)
    element.querySelector("img").src = previewUrl
    const label = element.querySelector("[data-attachment-name]")
    label.textContent = truncateFilename(name)
    label.title = name
    element.querySelector("[data-attachment-status]").textContent = uploading ? "Uploading…" : ""
    element.querySelector("button").setAttribute("aria-label", `Remove ${name}`)
    const input = element.querySelector("input")
    input.value = signedId || ""
    input.disabled = !signedId
    if (signedId) element.dataset.signedId = signedId
    this.attachmentsTarget.append(element)
    this.attachmentsTarget.hidden = false
    return element
  }

  removeAttachment(event) {
    const element = event.currentTarget.closest("[data-attachment]")
    if (element.dataset.uploadId) this.uploads.delete(element.dataset.uploadId)
    URL.revokeObjectURL(element.dataset.previewUrl)
    element.remove()
    this.attachmentsTarget.hidden = this.attachmentCount() === 0
    this.showAttachmentError("")
    this.saveDraft()
    this.inputTarget.focus()
  }

  attachmentCount() {
    return this.hasAttachmentsTarget ? this.attachmentsTarget.querySelectorAll("[data-attachment]").length : 0
  }

  attachedImages() {
    if (!this.hasAttachmentsTarget) return []
    return [...this.attachmentsTarget.querySelectorAll("[data-attachment][data-signed-id]")]
      .map(element => ({ name: element.dataset.name, previewUrl: element.dataset.previewUrl, signedId: element.dataset.signedId }))
  }

  showAttachmentError(message) {
    if (!this.hasAttachmentErrorTarget) return
    this.attachmentErrorTarget.textContent = message
    this.attachmentErrorTarget.hidden = !message
  }

  // Wait for pasted images to finish uploading, and don't send one that failed.
  guardSubmit(event) {
    if (!this.hasAttachmentsTarget) return
    if (this.uploads.size) {
      event.preventDefault()
      this.showAttachmentError("Wait for the images to finish uploading.")
    } else if (this.attachmentsTarget.querySelector("[data-attachment].is-failed")) {
      event.preventDefault()
      this.showAttachmentError("An image didn't upload. Remove it and paste it again.")
    }
  }

  submitting() {
    this.saveDraft()
    this.submittedText = this.inputTarget.value
    this.submittedImageIds = new Set(this.attachedImages().map(image => image.signedId))
    // Stop duplicate submits while allowing the user to continue drafting.
    this.submitTarget.disabled = true
  }

  submitted(event) {
    if (!event.detail.success) {
      this.submitTarget.disabled = false
      return
    }
    // The sent images are attached to the question now, so drop exactly those chips (resending one
    // would be refused). Images pasted while the question was in flight, still uploading or not,
    // stay for the next question, and so does text typed meanwhile.
    if (this.hasAttachmentsTarget) {
      this.attachmentsTarget.querySelectorAll("[data-attachment][data-signed-id]").forEach(element => {
        if (!this.submittedImageIds?.has(element.dataset.signedId)) return
        URL.revokeObjectURL(element.dataset.previewUrl)
        element.remove()
      })
      this.attachmentsTarget.hidden = this.attachmentCount() === 0
    }
    if (this.inputTarget.value === this.submittedText) this.inputTarget.value = ""
    this.saveDraft()
  }

  toggleRail() {
    const open = this.railTarget.classList.toggle("is-open")
    this.railToggleTarget.setAttribute("aria-expanded", String(open))
  }

  closeRail() {
    this.railTarget.classList.remove("is-open")
    this.railToggleTarget.setAttribute("aria-expanded", "false")
  }
}
